import NotificationList from "@src/features/app/top-nav/NotificationList";
import gql from "graphql-tag";
import { debounce } from "lodash";
import React from "react";
import {
  useGetNotificationsQuery,
  useMarkNotificationsReadMutation,
} from "./NotificationBell.generated";
import { Button } from "@src/lib/ui/Button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "@src/lib/ui/DropdownMenu";
import BellOutlineIcon from "@src/lib/icons/18px/bell-outline";
import Text from "@src/lib/ui/Text";
import { cn } from "@src/lib/utils/cn";

const NotificationDot: React.FC<{
  count?: number;
  className?: string;
  size: 8 | 12 | 16 | 24;
}> = (props) => {
  return (
    <div
      style={{ width: props.size, height: props.size }}
      className={cn(
        `bg-destructive rounded-full`,
        !!props.count && "flex items-center justify-center",
        props.className,
      )}
    >
      {props.count && (
        <Text variant="micro-title" className="font-semibold-v4 text-white">
          {props.count}
        </Text>
      )}
    </div>
  );
};

type Props = {
  dropdownSide?: "top" | "bottom";
};

export const NotificationBell: React.FC<Props> = ({ dropdownSide = "top" }) => {
  const PAGINATION_LIMIT = 15;
  const {
    data: newData,
    previousData,
    loading,
    fetchMore,
  } = useGetNotificationsQuery({
    variables: {
      pagination: {
        limit: PAGINATION_LIMIT,
        offset: 0,
      },
    },
  });
  const data = newData || previousData;
  const totalNotifications = React.useMemo(() => {
    return data?.Notifications.totalCount || 0;
  }, [data?.Notifications.totalCount]);
  const notifications = React.useMemo(() => {
    return data?.Notifications.objects || [];
  }, [data?.Notifications.objects]);
  const hasMore = React.useMemo(() => {
    return notifications.length < totalNotifications;
  }, [notifications.length, totalNotifications]);
  const totalUnreadNotifications = React.useMemo(() => {
    return data?.UnreadCount || 0;
  }, [data?.UnreadCount]);
  const [readNotificationIds, setReadNotificationIds] = React.useState<
    Set<string>
  >(new Set());
  const [markNotificationsRead] = useMarkNotificationsReadMutation({
    refetchQueries: ["GetNotifications"],
    variables: {
      ids: Array.from(readNotificationIds),
    },
    onCompleted: () => {
      setReadNotificationIds(new Set());
    },
  });
  const onNotificationRead = (id: string) => {
    setReadNotificationIds((prev) => new Set(prev).add(id));
  };
  const debouncedFetchMore = debounce(
    () =>
      fetchMore({
        variables: {
          pagination: {
            limit: PAGINATION_LIMIT,
            offset: notifications.length,
          },
        },
      }),
    500,
  );
  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (!open) {
          markNotificationsRead();
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button size="icon-md" variant="ghost" className="relative">
          <BellOutlineIcon />
          {totalUnreadNotifications >= 1 && (
            <NotificationDot
              size={totalUnreadNotifications > 9 ? 16 : 8}
              count={
                totalUnreadNotifications > 9
                  ? Math.min(totalUnreadNotifications, 99)
                  : undefined
              }
              className="absolute top-1 right-1"
            />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        side={dropdownSide}
        className="size-[400px]"
      >
        <NotificationList
          notifications={notifications}
          loading={loading}
          hasMore={hasMore}
          fetchMoreNotifications={() => {
            if (hasMore) {
              debouncedFetchMore();
            }
          }}
          onNotificationRead={onNotificationRead}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;

gql`
  query Notifications {
    AdminInProductNotifications {
      ...Notification
    }
  }
  query GetNotifications($pagination: PaginationInput) {
    Notifications: InProductNotifications(input: {}, pagination: $pagination) {
      totalCount
      objects {
        ...Notification
      }
    }
    UnreadCount: InProductNotificationUnreadCount
  }
  mutation MarkNotificationsRead($ids: [String!]!) {
    markInProductNotificationsAsRead(inProductNotificationIds: $ids) {
      success
    }
  }
`;
